import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { signUp } from '@/controller/profile';
import { toast } from '@/shared/components/UI';
import useCountry from '@/shared/hooks/useCountry';
import useMessageHook from '@/shared/hooks/useMessageHook';
import { useTranslations } from '@/translations/useTranslations.js';

const Signup = () => {
    const { t } = useTranslations();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const { requiredMsg, minMsg, emailMsg } = useMessageHook();

    // Form state
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',

        email: '',
        password: '',
        confirm_password: '',
        terms_and_condition: false,
        consent_data_processing: false,
        promotions: false,
    });

    // Form validation errors
    const [errors, setErrors] = useState({});
    const [signupLoading, setSignupLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkedTerm, setTerms] = useState(false);

    const today = new Date().toISOString().split('T')[0];

    const countryPhoneList = useCountry().getCountryPhone();
    const [phoneRules, setPhoneRules] = useState([]);

    const handleFieldChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData((prev) => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Required fields validation
        if (!formData.firstname.trim()) newErrors.firstname = requiredMsg('firstname');
        if (!formData.lastname.trim()) newErrors.lastname = requiredMsg('lastname');

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = requiredMsg('email');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = emailMsg('email');
        } else if (formData.email.length < 6) {
            newErrors.email = minMsg('email', '6');
        }

        // Password validation
        if (!formData.password.trim()) {
            newErrors.password = requiredMsg('password');
        } else if (formData.password.length < 8) {
            newErrors.password = minMsg('password', '8');
        }

        // Confirm password validation
        if (!formData.confirm_password.trim()) {
            newErrors.confirm_password = requiredMsg('confirm password');
        } else if (formData.confirm_password !== formData.password) {
            newErrors.confirm_password = 'The two passwords do not match!';
        }

        // Terms validation
        if (!formData.terms_and_condition) {
            newErrors.terms_and_condition = 'You must accept the terms and conditions';
        }

        // Consent Data Processing validation
        if (!formData.consent_data_processing) {
            newErrors.consent_data_processing = 'You must consent to data processing';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onFinish = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSignupLoading(true);
        toast.info('Registering... Please wait.');

        const data = {
            first_name: formData.firstname,
            last_name: formData.lastname,
            email: formData.email,
            password: formData.password,
            password_confirmation: formData.confirm_password,
            opt_in_newsletter: formData.promotions,
            terms: formData.terms_and_condition,
            consent_data_processing: formData.consent_data_processing,
            referral_code: searchParams.get('referral_code') || undefined,
        };

        try {
            const ret = await signUp(data);
            if (ret.status === 200) {
                toast.success('Registration successful! Please check your email for verification.');
                nav('/verification?email=' + encodeURIComponent(formData.email) + '&type=signup-success');
            } else {
                if (ret.msg === 'Email Already Taken') {
                    setErrors((prev) => ({ ...prev, email: 'This email is taken.' }));
                    toast.error('This email is already taken.');
                } else {
                    toast.error('Registration failed. Please try again.');
                }
            }
        } catch (error) {
            console.error('Signup error:', error);
            toast.error('An error occurred during registration. Please try again.');
        }

        setSignupLoading(false);
    };

    const checkTerms = (e) => {
        const checked = e.target.checked;
        setTerms(checked);
        handleFieldChange('terms_and_condition', checked);
    };

    const goPolicy = () => {
        let win = window.open('/footer-primary-links/privacy-policy', '_blank');
        if (win !== null) {
            win.focus();
        }
    };

    if (signupLoading) {
        return (
            <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
                <div className="flex flex-col w-full max-w-4xl py-5 flex-1 items-center justify-center">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-gray-600">Registering your account...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
            <div className="flex flex-col w-full max-w-4xl py-5 flex-1">
                {/* Header Section */}
                <div className="flex flex-wrap justify-between gap-3 p-4 mb-6">
                    <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                        {t('label.become_a_member')}
                    </p>
                    <p className="text-[#5f678c] text-sm font-normal leading-normal m-0">{t('label.signup_desc_2')}</p>
                </div>

                {/* Signup Form */}
                <form onSubmit={onFinish} className="space-y-4">
                    {/* Personal Information Section */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* First Name */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.firstname')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.firstname ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.firstname}
                                    onChange={(e) => handleFieldChange('firstname', e.target.value)}
                                    placeholder={t('form.firstname')}
                                    maxLength={50}
                                />
                                {errors.firstname && <p className="text-red-500 text-sm mt-1">{errors.firstname}</p>}
                            </div>

                            {/* Last Name */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.lastname')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.lastname ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.lastname}
                                    onChange={(e) => handleFieldChange('lastname', e.target.value)}
                                    placeholder={t('form.lastname')}
                                    maxLength={50}
                                />
                                {errors.lastname && <p className="text-red-500 text-sm mt-1">{errors.lastname}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Contact & Account Information Section */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact & Account Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Email */}
                            <div className="md:col-span-2">
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.email')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="email"
                                    className={`w-full h-14 rounded-xl border ${errors.email ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.email}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    placeholder="example@email.com"
                                    maxLength={100}
                                />
                                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                            </div>

                            {/* Password */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.password')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className={`w-full h-14 rounded-xl border ${errors.password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 transition-colors`}
                                        value={formData.password}
                                        onChange={(e) => handleFieldChange('password', e.target.value)}
                                        placeholder={t('form.password')}
                                        maxLength={50}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                    >
                                        {showPassword ? (
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.confirm_password')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        className={`w-full h-14 rounded-xl border ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 transition-colors`}
                                        value={formData.confirm_password}
                                        onChange={(e) => handleFieldChange('confirm_password', e.target.value)}
                                        placeholder={t('form.confirm_password')}
                                        maxLength={50}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                    >
                                        {showConfirmPassword ? (
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {errors.confirm_password && (
                                    <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Privacy & Terms Section */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {t('label.personal_data_and_privacy')}
                        </h3>

                        <div className="space-y-4">
                            {/* Terms and Conditions */}
                            <div>
                                <label className="flex items-start">
                                    <input
                                        type="checkbox"
                                        checked={formData.terms_and_condition}
                                        onChange={checkTerms}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                                    />
                                    <span className="ml-2 text-sm text-gray-900">
                                        {t('label.personal_data_confirmation')}
                                        <span
                                            className="text-blue-600 hover:text-blue-700 cursor-pointer underline ml-1"
                                            onClick={() => goPolicy()}
                                        >
                                            Privacy Policy.
                                        </span>
                                        <span className="text-red-500 ml-1">*</span>
                                    </span>
                                </label>
                                {errors.terms_and_condition && (
                                    <p className="text-red-500 text-sm mt-1">{errors.terms_and_condition}</p>
                                )}
                            </div>

                            {/* Consent to Data Processing */}
                            <div>
                                <label className="flex items-start">
                                    <input
                                        type="checkbox"
                                        checked={formData.consent_data_processing}
                                        onChange={(e) => handleFieldChange('consent_data_processing', e.target.checked)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                                    />
                                    <span className="ml-2 text-sm text-gray-900">
                                        I consent to the processing of my personal data.
                                        <span className="text-red-500 ml-1">*</span>
                                    </span>
                                </label>
                                {errors.consent_data_processing && (
                                    <p className="text-red-500 text-sm mt-1">{errors.consent_data_processing}</p>
                                )}
                            </div>

                            {/* Promotional Offers */}
                            <div>
                                <label className="flex items-start">
                                    <input
                                        type="checkbox"
                                        checked={formData.promotions}
                                        onChange={(e) => handleFieldChange('promotions', e.target.checked)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                                    />
                                    <span className="ml-2 text-sm text-gray-900">{t('label.promotion_offers')}</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="p-4 flex justify-center">
                        <button
                            type="submit"
                            disabled={!checkedTerm || signupLoading}
                            className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                        >
                            <span className="overflow-hidden">
                                {signupLoading ? 'Registering...' : t('label.signup_now')}
                            </span>
                        </button>
                    </div>

                    {/* Login Link */}
                    <div className="px-4 pb-4">
                        <div className="text-center">
                            <p className="text-sm text-gray-600">
                                Already have an account?{' '}
                                <button
                                    type="button"
                                    onClick={() => nav('/')}
                                    className="text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200 hover:underline"
                                >
                                    Login here
                                </button>
                            </p>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Signup;
